import { useContext, useEffect, useMemo } from "react";
import { AppContext } from "../App";
import {
  CDN_URL,
  formatDate,
  getInlineLoader,
  getPublishedItems,
} from "../Helpers";
import Footer from "../ones/Footer";
import CallToAction from "../ones/callToAction";

export default function NewsDetails({ newsSlug }) {
  const appContext = useContext(AppContext);

  useEffect(() => {
    if (!appContext.allNews) {
      appContext.getAllNews();
    }
  }, [appContext]);

  const publishedNews = useMemo(
    () => getPublishedItems(appContext.allNews || []),
    [appContext.allNews],
  );

  const newsItem = useMemo(
    () =>
      publishedNews.find(
        (item) => item.slug === newsSlug || item.uuid === newsSlug,
      ),
    [publishedNews, newsSlug],
  );

  const normalizeRichBody = (html = "") => {
    const rawBody = String(html).trim();
    if (!rawBody) {
      return "";
    }

    if (!rawBody.includes("<")) {
      return rawBody;
    }

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(rawBody, "text/html");

      doc.querySelectorAll("a").forEach((link) => {
        if (!link.target) {
          link.target = "_blank";
        }
        const rel = new Set((link.rel || "").split(" ").filter(Boolean));
        rel.add("noopener");
        rel.add("noreferrer");
        link.rel = Array.from(rel).join(" ");
      });

      doc.querySelectorAll("img").forEach((img) => {
        const src = img.getAttribute("src") || "";
        if (src.startsWith("/") && !src.startsWith("//")) {
          img.src = `${CDN_URL.replace(/\/$/, "")}${src}`;
        }
        if (!img.alt) {
          img.alt = newsItem?.title || "News image";
        }
      });

      return doc.body.innerHTML;
    } catch (error) {
      return rawBody;
    }
  };

  const richBodyHtml = useMemo(
    () => normalizeRichBody(newsItem?.body || ""),
    [newsItem],
  );

  if (!appContext.allNews) {
    return <div className="container mSupportLoading">{getInlineLoader()}</div>;
  }

  if (!newsItem) {
    return (
      <section className="ags-section py-6">
        <div className="container">
          <div className="ags-empty-state-minimal text-center">
            <p className="ags-section-kicker mb-2">Article Not Found</p>
            <h2 className="font-bold mb-3">We could not locate this update</h2>
            <p className="text-muted mb-4">
              The article you are looking for may have been moved or archived.
            </p>
            <button
              type="button"
              className="btn btn-primary btn-rounded btn-padded"
              onClick={() => appContext.navTo({ item: "news" })}
            >
              Back to News & Updates
            </button>
          </div>
        </div>
      </section>
    );
  }

  const relatedNews = publishedNews
    .filter(
      (item) => item.uuid !== newsItem.uuid && item.slug !== newsItem.slug,
    )
    .slice(0, 3);

  return (
    <>
      <section className="ags-news-detail-section">
        <header className="ags-news-detail-header">
          <div className="container">
            <div className="row">
              <div className="col-lg-10 col-xl-8">
                <div className="ags-news-meta mb-3">
                  <span className="ags-meta-label">Published in:</span>
                  <span className="ags-meta-divider">/</span>
                  <time className="ags-meta-date">
                    {formatDate(newsItem.dateCreated, true)}
                  </time>
                </div>
                <h1
                  className="ags-news-main-title font-bold mb-0"
                  data-aos="fade-up"
                >
                  {newsItem.title}
                </h1>
              </div>
            </div>
          </div>
        </header>

        <div className="container mb-5" data-aos="fade-up" data-aos-delay="100">
          <div className="ags-news-large-frame">
            <img
              src={`${CDN_URL}${newsItem.coverPhoto}`}
              alt={newsItem.title}
              className="ags-news-hero-img"
            />
          </div>
        </div>

        {newsItem.publication ? (
          <div className="container mb-4">
            <div className="p-3 bg-light rounded-3">
              <strong>Publication: </strong>
              <a
                href={`${CDN_URL}${newsItem.publication}`}
                target="_blank"
                rel="noreferrer noopener"
                className="ags-pdf-download-strip"
              >
                <div className="ags-pdf-left-block">
                  {/* Clean Vector PDF Icon */}
                  <svg
                    className="ags-pdf-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                  </svg>

                  <div className="ags-pdf-text-meta">
                    <span className="ags-pdf-label">
                      Attachment Documentation
                    </span>
                    <h5 className="ags-pdf-filename font-bold mb-0">
                      {newsItem.publication.split("/").pop() ||
                        "AGS Document.pdf"}
                    </h5>
                  </div>
                </div>

                <div className="ags-pdf-right-block">
                  <span className="ags-download-action-text">
                    Download Share
                  </span>
                  <svg
                    className="ags-download-arrow"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <polyline points="19 12 12 19 5 12"></polyline>
                  </svg>
                </div>
              </a>
            </div>
          </div>
        ) : null}

        <div className="container pb-6">
          <div className="row g-5 lg-gap-6">
            <main className="col-lg-8">
              <article
                className="ags-news-rich-body"
                data-aos="fade-up"
                data-aos-delay="150"
              >
                {richBodyHtml.includes("<") ? (
                  <div dangerouslySetInnerHTML={{ __html: richBodyHtml }} />
                ) : (
                  <p className="lead-paragraph">{richBodyHtml}</p>
                )}
              </article>

              <div className="ags-news-footer-nav mt-5 pt-4">
                <button
                  className="ags-back-link"
                  onClick={() => appContext.navTo({ item: "news" })}
                >
                  ← Return to updates
                </button>
              </div>
            </main>

            <aside className="col-lg-4">
              <div
                className="ags-news-sidebar-sticky"
                data-aos="fade-left"
                data-aos-delay="200"
              >
                <div className="ags-sidebar-panel-minimal mb-5">
                  <h5 className="ags-sidebar-heading mb-3">Release Summary</h5>
                  <div className="ags-meta-stat-row">
                    <span className="stat-title">Source:</span>
                    <span className="stat-value">AGS Global Systems</span>
                  </div>
                  <div className="ags-meta-stat-row">
                    <span className="stat-title">Distribution:</span>
                    <span className="stat-value">Public Announcement</span>
                  </div>
                </div>

                {relatedNews.length > 0 && (
                  <div className="ags-sidebar-panel-minimal">
                    <h5 className="ags-sidebar-heading mb-4">Latest Stories</h5>
                    <div className="ags-sidebar-feed">
                      {relatedNews.map((item) => (
                        <div
                          className="ags-feed-item"
                          key={item.uuid || item.slug}
                          role="button"
                          tabIndex={0}
                          onClick={() =>
                            appContext.navTo({
                              item: "news",
                              subItem: item.slug || item.uuid,
                            })
                          }
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              appContext.navTo({
                                item: "news",
                                subItem: item.slug || item.uuid,
                              });
                            }
                          }}
                        >
                          <span className="ags-feed-date">
                            {new Date(item.dateCreated).toLocaleDateString(
                              "en-US",
                              {
                                month: "short",
                                day: "numeric",
                              },
                            )}
                          </span>
                          <h6 className="ags-feed-title mt-1 mb-0">
                            {item.title}
                          </h6>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </aside>
          </div>
        </div>
      </section>
      <CallToAction />
      <Footer />
    </>
  );
}
